/**
 * MedCaddy API Client - Permissions Module
 * Handles permissions CRUD and authorization PDF download.
 */

import { apiFetch, assertOk, assertOkWithDetail } from './core';

// Permissions API
export const permissions = {
  async list(filters = {}) {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.search) params.append('search', filters.search);

    const response = await apiFetch(`/permissions?${params}`);
    assertOk(response, 'Failed to fetch permissions');
    return response.json();
  },

  async create(permission) {
    const response = await apiFetch('/permissions', {
      method: 'POST',
      body: JSON.stringify(permission)
    });
    await assertOkWithDetail(response, 'Failed to create permission');
    return response.json();
  },

  async revoke(id, reason = '', notifyProvider = true) {
    const response = await apiFetch(`/permissions/${id}`, {
      method: 'DELETE',
      body: JSON.stringify({ reason, notifyProvider })
    });
    assertOk(response, 'Failed to revoke permission');
  },

  /** Purpose: Fetches authorization PDF for one permission. Inputs: id. Output: Promise<{ blob: Blob, fileName: string, fileType: string }>. */
  async getPdf(id) {
    const response = await apiFetch(`/authorizations/${id}/pdf`);
    assertOk(response, 'Failed to fetch authorization PDF');

    const blob = await response.blob();
    const contentDisposition = response.headers.get('content-disposition');
    let fileName = `authorization-${id}.pdf`;

    if (contentDisposition) {
      const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
      if (utf8Match?.[1]) {
        try {
          fileName = decodeURIComponent(utf8Match[1]);
        } catch (_error) {
          fileName = utf8Match[1];
        }
      } else {
        const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
        if (fileNameMatch?.[1]) {
          fileName = fileNameMatch[1];
        }
      }
    }

    return {
      blob,
      fileName,
      fileType: response.headers.get('content-type') || blob.type || 'application/pdf'
    };
  }
};
